/* =============================================================
   AuthProvider — single source of truth for the current session.

   Public API preserved from the Supabase version: useAuth(),
   useRequireAuth(), useRequireApproved(). Pages should not need
   any changes.

   Backend: Microsoft Entra External ID via MSAL.js. We subscribe
   to MSAL events and surface a minimal { user, session, loading,
   signOut } shape that matches what the pages already access:
     - user.id (from `localAccountId` = Entra `oid`)
     - user.email (from `username`)
     - user.user_metadata.full_name (from idTokenClaims.name)
   ============================================================ */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { EventType, type AccountInfo } from '@azure/msal-browser';
import { msalInstance, msalReady, API_SCOPE, signOutEverywhere } from './msal';
import { apiFetch, ApiError } from './api';
import { migrateLocalToServer } from './psychLocal';

export interface AuthUser {
  /** Entra `oid` — the canonical, stable, tenant-wide GUID. */
  id: string;
  email: string | null;
  user_metadata: { full_name?: string };
}

export interface AuthSession {
  user: AuthUser;
}

interface AuthContextValue {
  /** null while the first MSAL hydration is in flight; { ... } once we know. */
  session: AuthSession | null;
  user: AuthUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

function toAuthUser(acc: AccountInfo): AuthUser {
  const claims = (acc.idTokenClaims ?? {}) as Record<string, unknown>;
  const fullName =
    (typeof claims.name === 'string' && claims.name) ||
    (typeof claims.given_name === 'string' && claims.given_name) ||
    undefined;
  return {
    id: acc.localAccountId, // = oid
    email: acc.username || null,
    user_metadata: fullName ? { full_name: fullName } : {},
  };
}

/* Create/refresh the person's account row (POST /api/auth/bootstrap) and move
   any guest assessment answers into the account — once per tab per account.
   Idempotent on the server, so a repeat after a reload is harmless. */
function ensureBootstrapped(acc: AccountInfo) {
  const key = `auth-bootstrapped:${acc.localAccountId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    /* storage blocked: bootstrap anyway */
  }
  apiFetch('/api/auth/bootstrap', {
    method: 'POST',
    body: { full_name: acc.idTokenClaims?.name ?? null },
  }).catch((e) => {
    try {
      sessionStorage.removeItem(key); // try again on the next page load
    } catch {
      /* ignore */
    }
    console.warn('[auth] bootstrap failed:', e);
  });
  migrateLocalToServer(acc.localAccountId).catch((e) => {
    console.warn('[auth] Psych Safety local->server migration failed:', e);
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    msalReady.then(() => {
      if (!mounted) return;
      const acc = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
      if (acc) {
        msalInstance.setActiveAccount(acc);
        setUser(toAuthUser(acc));
        /* The LOGIN_SUCCESS event fires while msal.ts handles the redirect,
           before this listener exists, so make sure the account row exists
           here too (once per tab). */
        ensureBootstrapped(acc);
      }
      setLoading(false);
    });

    /* React to live sign-in / sign-out / token-refresh events.
       CRITICAL: only call setUser if the account identity changed.
       Otherwise we'd hand React a fresh object on every token
       acquisition, every effect that depends on auth.user re-runs,
       each of those calls apiFetch, apiFetch triggers another token
       acquisition, and the loop runs ~10x per second until the
       backend tips over. */
    let currentAccountId: string | null = null;
    const callbackId = msalInstance.addEventCallback((evt) => {
      if (!mounted) return;
      if (
        evt.eventType === EventType.LOGIN_SUCCESS ||
        evt.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        evt.eventType === EventType.SSO_SILENT_SUCCESS
      ) {
        const acc =
          evt.payload && 'account' in evt.payload
            ? ((evt.payload as { account: AccountInfo | null }).account)
            : null;
        if (acc && acc.localAccountId !== currentAccountId) {
          currentAccountId = acc.localAccountId;
          msalInstance.setActiveAccount(acc);
          setUser(toAuthUser(acc));
          ensureBootstrapped(acc);
        }
      }
      if (evt.eventType === EventType.LOGOUT_SUCCESS) {
        currentAccountId = null;
        setUser(null);
      }
    });

    return () => {
      mounted = false;
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  const signOut = useCallback(() => signOutEverywhere(), []);

  const value: AuthContextValue = {
    session: user ? { user } : null,
    user,
    loading,
    signOut,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(Ctx);
}

/** Convenience guard: redirect if not authed. Use in protected pages.
    When redirecting to /account, preserves the current URL as ?next= so
    the sign-in flow returns the user to where they were trying to go. */
export function useRequireAuth(redirectTo = '/account') {
  const auth = useAuth();
  useEffect(() => {
    if (!auth.loading && !auth.user) {
      let target = redirectTo;
      if (redirectTo === '/account' && typeof window !== 'undefined') {
        const here = window.location.pathname + window.location.search;
        if (here && here !== '/' && !/^\/account(\?|$)/.test(here)) {
          target = `/account?next=${encodeURIComponent(here)}`;
        }
      }
      window.location.replace(target);
    }
  }, [auth.loading, auth.user, redirectTo]);
  return auth;
}

/**
 * Auth + approval guard. Bounces unauthed users to /account and
 * unapproved users to /dashboard (which renders the "pending"
 * panel). Used on every CYBHI page.
 *
 * The effect depends on `userId` (string), not `auth.user` (object).
 * MSAL recreates the user object every time a fresh access token is
 * minted — depending on the object identity would cause infinite
 * re-fetches.
 */
export function useRequireApproved() {
  const auth = useRequireAuth();
  const userId = auth.user?.id ?? null;
  const [approved, setApproved] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!userId) {
      setApproved(undefined);
      return;
    }
    let cancelled = false;
    apiFetch<boolean>('/api/me/approved')
      .then((data) => {
        if (cancelled) return;
        setApproved(!!data);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = e instanceof ApiError ? e.status : 0;
        if (status === 401) return;
        console.warn('[auth] /me/approved failed:', e);
        setApproved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (auth.loading) return;
    if (!userId) return;
    if (approved === false) {
      window.location.replace('/account/teams');
    }
  }, [auth.loading, userId, approved]);

  return { ...auth, approved };
}

/* Re-exported for pages that need to start a sign-in directly
   (AccountPage, AcceptInvitePage). Wraps msalInstance
   so callers don't have to import @azure/msal-browser. */
export async function startSignIn(next: string = '/account'): Promise<void> {
  await msalReady;
  await msalInstance.loginRedirect({
    scopes: [API_SCOPE],
    state: next,
    prompt: 'select_account',
  });
}

/* Change password: Entra External ID has no direct "change password"
   page, so we reopen its sign-in screen for this account. There the
   person chooses "Forgot password?" and resets it with an email code. */
export async function startPasswordChange(email: string | null): Promise<void> {
  await msalReady;
  await msalInstance.loginRedirect({
    scopes: [API_SCOPE],
    state: '/account',
    prompt: 'login',
    loginHint: email ?? undefined,
  });
}
